// Decision card — a conversation that needs a judgement call from Kimi.
// Lives in the "Needs You" section of Standup.
//
// Phase 6b adds optional multi-select: when `selection` is provided the card
// renders a checkbox next to the tag row. Clicking the checkbox toggles
// selection without opening the thread. Clicking anywhere else on the card
// still opens the thread as before. When the card is selected we apply a
// subtle inset ring + tinted background so the bulk-action bar's count
// corresponds to something visibly chosen on screen.

import { Tag } from '@/components/brand/Tag';
import type { TagVariant } from '@/components/brand/Tag';
import type { Channel, Brand } from '@md/shared';
import type { AgeState } from '@/lib/display';

export type DecideCardData = {
  id: string;
  brand: Brand;
  channel: Channel;
  customerName: string;
  subjectLine: string; // may be empty — we fall back to first 80 chars of excerpt
  excerpt: string;
  ageLabel: string; // e.g. "2h", "5h", "18h"
  ageWarn?: boolean;
  /**
   * Coarse age bucket for the #22 color-coding dot. Independent of ageWarn:
   * ageWarn controls amber label text (>= 4h); ageState drives a green /
   * yellow / red dot that reads at-a-glance without numerals.
   */
  ageState?: AgeState;
  extraTags?: string[]; // e.g. "VIP · 4 orders"
  reason?: string; // the "because" / explainer text
  address?: string; // inbox address, etsy shop, etc.
  /**
   * Phase 6i. One-sentence Haiku summary of the thread, replaces the raw
   * subject line as the card's hero text. Falls back to subjectLine + name
   * when null.
   */
  aiSummary?: string | null;
  /**
   * A contiguous substring of aiSummary to italicize. Null/undefined when
   * there's no emphasis phrase.
   */
  aiHighlight?: string | null;
  /**
   * Phase 9. Detected intent chip — tracking / damage / address_change / etc.
   * Null means no notable intent (aka "generic"). When set, renders as a
   * colored pill in the tag row. Tooltip shows the evidence phrase.
   */
  intentChip?: {
    intent: string;
    label: string;
    chipClass: string;
    evidence: string | null;
  } | null;
};

type DecideCardProps = {
  data: DecideCardData;
  onOpen: (id: string) => void;
  /**
   * Phase 6b. When present, the card shows a checkbox and joins bulk-select.
   * Omit entirely to get the pre-6b behavior (no checkbox, no selection).
   *
   * `selectionActive` (set by Standup when at least one card is already
   * selected) flips the card-body click behavior: instead of opening the
   * thread, clicking anywhere on the card toggles its selection. The
   * "Open thread" button still opens regardless so Kimi can always escape.
   */
  selection?: {
    selected: boolean;
    onToggle: (id: string) => void;
    selectionActive?: boolean;
  };
};

// NOTE: 'shipstation' is a Channel for auth-storage only — it never shows up as
// a conversation channel so rendering-side maps below just need a harmless
// fallback.
const CHANNEL_LABEL: Record<Channel, string> = {
  gmail: 'Gmail',
  etsy_md: 'Etsy',
  etsy_hk: 'Etsy',
  amazon: 'Amazon',
  shopify_md: 'Shopify',
  shopify_brokerage: 'Shopify',
  shipstation: 'ShipStation',
};

const CHANNEL_TAG: Record<Channel, TagVariant> = {
  gmail: 'ch-gmail',
  etsy_md: 'ch-etsy',
  etsy_hk: 'ch-etsy',
  amazon: 'ch-amzn',
  shopify_md: 'ch-shop',
  shopify_brokerage: 'ch-shop',
  shipstation: 'ch-shop',
};

/**
 * Render the hero headline. When an AI summary is present we use it and
 * italicize the highlight substring; otherwise we fall back to the classic
 * "Customer Name — subject" shape.
 */
function HeroHeadline({
  customerName,
  subjectLine,
  aiSummary,
  aiHighlight,
}: {
  customerName: string;
  subjectLine: string;
  aiSummary: string | null | undefined;
  aiHighlight: string | null | undefined;
}) {
  if (aiSummary && aiSummary.trim().length > 0) {
    const highlight = aiHighlight && aiSummary.includes(aiHighlight) ? aiHighlight : null;
    if (!highlight) {
      return <h3>{aiSummary}</h3>;
    }
    const i = aiSummary.indexOf(highlight);
    const before = aiSummary.slice(0, i);
    const after = aiSummary.slice(i + highlight.length);
    return (
      <h3>
        {before}
        <em>{highlight}</em>
        {after}
      </h3>
    );
  }
  return (
    <h3>
      {customerName}
      {subjectLine ? ' — ' : ''}
      {subjectLine && <em>{subjectLine}</em>}
    </h3>
  );
}

// Human-readable tooltip text for the colored age dot. Mirrors the #22 scope:
// green <24h, yellow 1–3d, red >3d unreplied.
const AGE_DOT_TITLE: Record<AgeState, string> = {
  fresh: 'Under 24 hours old',
  aging: '1 to 3 days old',
  stale: 'Over 3 days old',
};

export function DecideCard({ data, onOpen, selection }: DecideCardProps) {
  const { id, brand, channel, customerName, subjectLine, excerpt, ageLabel, ageWarn, ageState, extraTags, reason, address, aiSummary, aiHighlight, intentChip } = data;
  const selected = selection?.selected ?? false;
  const selectionActive = selection?.selectionActive ?? false;
  // When bulk selection is active, clicking anywhere on the card toggles
  // selection rather than opening the thread. Keeps the "Open thread"
  // button as the explicit escape hatch.
  const handleBodyClick = () => {
    if (selection && selectionActive) selection.onToggle(id);
    else onOpen(id);
  };

  return (
    <article
      className={`decide-card${selected ? ' decide-card--selected' : ''}`}
      onClick={handleBodyClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleBodyClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-selected={selection ? selected : undefined}
      style={
        selected
          ? {
              boxShadow: 'inset 0 0 0 2px hsl(var(--monstera))',
              background: 'hsl(var(--monstera) / 0.04)',
            }
          : undefined
      }
    >
      <div className="decide-top">
        <div className="tag-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {selection ? (
            <label
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'pointer',
                marginRight: 2,
              }}
              aria-label={selected ? 'Deselect thread' : 'Select thread'}
            >
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => {
                  e.stopPropagation();
                  selection.onToggle(id);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: 16,
                  height: 16,
                  cursor: 'pointer',
                  accentColor: 'hsl(var(--monstera))',
                }}
              />
            </label>
          ) : null}
          <Tag variant={brand === 'md' ? 'brand-md' : 'brand-hk'}>
            {brand === 'md' ? 'MD' : 'HK'}
          </Tag>
          <Tag variant={CHANNEL_TAG[channel]}>{CHANNEL_LABEL[channel]}</Tag>
          {intentChip ? (
            <span
              className={`intent-chip ${intentChip.chipClass}`}
              title={intentChip.evidence ?? intentChip.label}
            >
              {intentChip.label}
            </span>
          ) : null}
          {extraTags?.map((t) => (
            <Tag key={t} variant="neutral">{t}</Tag>
          ))}
        </div>
        <span
          className={ageWarn ? 'age warn' : 'age'}
          title={ageState ? AGE_DOT_TITLE[ageState] : undefined}
        >
          {ageState ? (
            <span className={`age-dot age-dot--${ageState}`} aria-hidden />
          ) : null}
          {ageLabel}
        </span>
      </div>

      <HeroHeadline
        customerName={customerName}
        subjectLine={subjectLine}
        aiSummary={aiSummary}
        aiHighlight={aiHighlight}
      />

      <p className="excerpt">{excerpt}</p>

      <div className="actions-row">
        <button
          type="button"
          className="btn primary"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(id);
          }}
        >
          Open thread
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>

      {(reason || address) && (
        <div className="card-foot">
          {reason ? <span className="because">{reason}</span> : <span />}
          {address ? <span>{address}</span> : null}
        </div>
      )}
    </article>
  );
}
